import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { auth } from '../firebase/config';
import toast from 'react-hot-toast';

// TODO: Mover a variable de entorno
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_test_placeholder');

const CheckoutForm = ({ plan, cycle }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) return;

    setIsProcessing(true);
    setErrorMessage(null);

    // 1. Validar el PaymentElement
    const { error: submitError } = await elements.submit();
    if (submitError) {
      setErrorMessage(submitError.message);
      setIsProcessing(false);
      return;
    }

    try {
      const user = auth.currentUser;
      const token = await user.getIdToken();

      // 2. Llamar al backend para crear la suscripción con Trial
      const response = await fetch('/api/create-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          planId: plan,
          billingCycle: cycle,
        }),
      });

      const session = await response.json();

      if (!response.ok) {
        throw new Error(session.error || 'Error al iniciar la suscripción');
      }

      // Si no hay clientSecret pero ya es trialing/active, redirigir
      if (!session.clientSecret && (session.status === 'trialing' || session.status === 'active')) {
        navigate('/dashboard?status=success');
        return;
      }

      // 3. Confirmar el pago / Setup de suscripción
      const { error } = await (session.clientSecret.startsWith('seti_') 
        ? stripe.confirmSetup({
            elements,
            clientSecret: session.clientSecret,
            confirmParams: {
              return_url: `${window.location.origin}/dashboard?status=success`,
            },
          })
        : stripe.confirmPayment({
            elements,
            clientSecret: session.clientSecret,
            confirmParams: {
              return_url: `${window.location.origin}/dashboard?status=success`,
            },
          }));

      if (error) {
        setErrorMessage(error.message);
      }
    } catch (err) {
      console.error('Error:', err);
      setErrorMessage(err.message);
      toast.error(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100 mb-6">
        <h3 className="font-bold text-gray-900 mb-2">Resumen de tu plan</h3>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500 uppercase tracking-wider font-black text-[10px]">Plan seleccionado:</span>
          <span className="font-bold text-primary-600 uppercase">{plan === 'organizer' ? 'Organizador' : plan}</span>
        </div>
        <div className="flex justify-between text-sm mt-2">
          <span className="text-gray-500 uppercase tracking-wider font-black text-[10px]">Periodo de prueba:</span>
          <span className="font-bold text-emerald-600">14 Días GRATIS</span>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-[10px] text-gray-400 leading-relaxed italic">
            * No se te cobrará nada hoy. Tras los 14 días de prueba, se te facturará {cycle === 'yearly' ? '290€/año' : '29€/mes'} automáticamente a menos que canceles antes.
          </p>
        </div>
      </div>

      <PaymentElement options={{ layout: 'tabs' }} />
      
      {errorMessage && (
        <div className="p-4 bg-red-50 text-red-600 text-sm rounded-xl border border-red-100 font-medium">
          {errorMessage}
        </div>
      )}

      <button
        disabled={!stripe || isProcessing}
        className={`w-full bg-primary-500 hover:bg-primary-600 text-white font-black py-4 px-6 rounded-full shadow-xl shadow-primary-500/20 transition-all transform active:scale-95 uppercase tracking-widest text-xs ${isProcessing ? 'opacity-70 cursor-wait' : ''}`}
      >
        {isProcessing ? 'Procesando...' : 'Comenzar 14 días gratis'}
      </button>
    </form>
  );
};

const CheckoutSubscription = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const plan = searchParams.get('plan') || 'organizer';
  const cycle = searchParams.get('cycle') || 'monthly';
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          const { doc, getDoc } = await import('firebase/firestore');
          const { db } = await import('../firebase/config');
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          
          if (userDoc.exists()) {
            const data = userDoc.data();
            const status = data.subscription?.status;
            if (status === 'active' || status === 'trialing') {
              console.log('CheckoutSubscription: User already subscribed, redirecting...');
              navigate('/dashboard');
              return;
            }
          }
        } catch (err) {
          console.error('Error checking sub:', err);
        }
      } else {
        // Si no hay usuario y no está cargando, redirigir a login si es necesario 
        // o simplemente dejar de cargar para mostrar el estado de error de Stripe
        console.log('CheckoutSubscription: No user found');
      }
      setChecking(false);
    });
    return () => unsubscribe();
  }, [navigate]);

  if (checking) return <div className="min-h-screen flex items-center justify-center font-bold text-gray-400">Verificando estado...</div>;

  // Opciones de apariencia para Stripe
  const options = {
    mode: 'subscription',
    amount: cycle === 'yearly' ? 29000 : 2900,
    currency: 'eur',
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#10b981', // emerald-500 (color primario de la app)
        borderRadius: '12px',
      },
    },
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="text-center mb-10">
          <img src="/visiopadel-logo.png" alt="VisioPadel" className="h-20 mx-auto mb-6" />
          <h2 className="text-3xl font-black text-gray-900 tracking-tight">Finaliza tu suscripción</h2>
          <p className="mt-2 text-gray-500 font-medium">Introduce tus datos de pago para activar la prueba</p>
        </div>

        <div className="bg-white p-8 rounded-3xl shadow-2xl border border-gray-100">
          <Elements stripe={stripePromise} options={options}>
            <CheckoutForm plan={plan} cycle={cycle} />
          </Elements>
        </div>

        <p className="mt-8 text-center text-xs text-gray-400">
          Pago seguro procesado por Stripe. Puedes cancelar en cualquier momento desde tu panel de gestión.
        </p>
      </div>
    </div>
  );
};

export default CheckoutSubscription;
